/**
 * Paginate a Supabase query builder using `.range()`.
 *
 * The `build` callback must return a fresh query builder each time it is
 * invoked (so callers avoid accidentally mutating shared query state). It is
 * given the current [from, to] range; typically callers only need to append
 * `.range(from, to)` to the query they already constructed.
 *
 * Fetching stops when a page returns fewer than `pageSize` rows.
 */
export async function paginateSupabase<T>(
  build: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
  opts: { pageSize?: number; maxRows?: number } = {}
): Promise<T[]> {
  const pageSize = opts.pageSize ?? 1000;
  const maxRows = opts.maxRows ?? 200_000;

  const out: T[] = [];
  let from = 0;

  while (out.length < maxRows) {
    const to = from + pageSize - 1;
    const { data, error } = await build(from, to);
    if (error) throw error;

    const rows = (data ?? []) as T[];
    out.push(...rows);

    if (rows.length < pageSize) break;
    from += pageSize;
  }

  return out;
}
